import { useCallback, useRef, useState } from "react";
import { Upload, Alert, Button, Typography, message, theme } from "antd";
import { InboxOutlined, FilePdfOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";
import { useTranslation } from "react-i18next";
import { WizardStep } from "../../BookingWizard/WizardStep";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { useCompany } from "../../../hooks/useCompany";
import { useBookingWizardV2 } from "../context/BookingWizardV2Context";
import { InvoiceAnalysisAnimation } from "../components/InvoiceAnalysisAnimation";
import { BookingDraftPreview } from "../components/BookingDraftPreview";
import {
  analyzeStoredCommercialInvoice,
  fetchCachedCommercialInvoiceAnalysis,
} from "../../../features/commercialInvoiceAnalysis/commercialInvoiceAnalysisService";
import { getCachedAnalysisForPath } from "../../../features/commercialInvoiceAnalysis/commercialInvoiceAnalysisCache";
import { uploadCommercialInvoiceWithCompanyPath } from "../../../utils/commercialInvoice";
import {
  ensureDraftBooking,
  markV2Booking,
} from "../utils/ensureDraftBooking";
import { mapAnalysisToBooking } from "../utils/mapAnalysisToBooking";
import { useIncoterms } from "../../../hooks/useIncoterms";
import { useTransportTypes } from "../../../hooks/useTransportTypes";
import { useBookingTypes } from "../../../hooks/useBookingTypes";

const { Dragger } = Upload;
const { Paragraph } = Typography;

const ACCEPTED_TYPES = ".pdf,.doc,.docx,.xls,.xlsx";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const InvoiceUploadStep = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { state, updateStep, dispatch } = useBookingWizard();
  const { selectedCompany } = useCompany();
  const { incoterms } = useIncoterms();
  const { transportTypes } = useTransportTypes();
  useBookingTypes();

  const {
    analysisPhase,
    setAnalysisPhase,
    setAnalysisResult,
    analysisResult,
    setAnalysisError,
    analysisError,
    setMissingFields,
    setUploadedFileName,
    uploadedFileName,
    setV2Step,
    missingFields,
  } = useBookingWizardV2();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [existingPath] = useState(state.commercial_invoice_file ?? null);
  const analyzingRef = useRef(false);

  const runAnalysis = useCallback(
    async (file: File, bookingId: number) => {
      setIsProcessing(true);
      setAnalysisError(null);
      setUploadedFileName(file.name);
      setAnalysisResult(null);
      setMissingFields([]);

      try {
        setAnalysisPhase("uploading");

        const filePath = await uploadCommercialInvoiceWithCompanyPath(
          file,
          selectedCompany!,
          bookingId
        );

        if (!filePath) {
          throw new Error(t("bookingWizardV2.invoice.uploadFailed"));
        }

        markV2Booking(bookingId);
        dispatch({ type: "SET_BOOKING_ID", payload: bookingId });

        setAnalysisPhase("extracting");
        await sleep(400);

        setAnalysisPhase("analyzing");
        const { data, error } = await analyzeStoredCommercialInvoice(
          { bookingId, objectPath: filePath },
          { deepCheck: true }
        );

        if (error || !data) {
          throw new Error(error ?? t("bookingWizardV2.invoice.analysisFailed"));
        }

        setAnalysisPhase("mapping");
        await sleep(500);

        const mapped = mapAnalysisToBooking(data, incoterms, transportTypes);
        setMissingFields(mapped.missingFields);
        setAnalysisResult(data);

        await updateStep(1, {
          id: bookingId,
          commercial_invoice_file: filePath,
          reference: mapped.reference ?? state.reference,
          transport_type: mapped.transport.transport_type,
          booking_type: mapped.transport.booking_type ?? undefined,
          booking_incoterm: mapped.transport.booking_incoterm,
          products: mapped.products,
        });

        setAnalysisPhase("complete");
        message.success(t("bookingWizardV2.invoice.analysisComplete"));
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : t("bookingWizardV2.invoice.analysisFailed");
        setAnalysisError(msg);
        setAnalysisPhase("error");
        message.error(msg);
      } finally {
        setIsProcessing(false);
      }
    },
    [
      dispatch,
      incoterms,
      selectedCompany,
      setAnalysisError,
      setAnalysisPhase,
      setAnalysisResult,
      setMissingFields,
      setUploadedFileName,
      state.reference,
      t,
      transportTypes,
      updateStep,
    ]
  );

  const startAnalysis = useCallback(
    async (file: File) => {
      if (analyzingRef.current) return;

      analyzingRef.current = true;
      setSelectedFile(file);
      setAnalysisError(null);

      try {
        const bookingId = await ensureDraftBooking(selectedCompany, state.id);
        await runAnalysis(file, bookingId);
      } catch (err) {
        message.error(
          err instanceof Error ? err.message : t("bookingWizardV2.invoice.uploadFailed")
        );
      } finally {
        analyzingRef.current = false;
      }
    },
    [runAnalysis, selectedCompany, setAnalysisError, state.id, t]
  );

  const loadExistingAnalysis = useCallback(async () => {
    if (!state.id || !existingPath) return;

    setIsProcessing(true);
    try {
      const { data: cachedRow } = await fetchCachedCommercialInvoiceAnalysis(
        state.id
      );
      const cached = getCachedAnalysisForPath(cachedRow, existingPath);
      if (cached) {
        const mapped = mapAnalysisToBooking(cached, incoterms, transportTypes);
        setAnalysisResult(cached);
        setMissingFields(mapped.missingFields);
        setUploadedFileName(cached.fileName);
        setAnalysisPhase("complete");
      }
    } finally {
      setIsProcessing(false);
    }
  }, [
    existingPath,
    incoterms,
    setAnalysisPhase,
    setAnalysisResult,
    setMissingFields,
    setUploadedFileName,
    state.id,
    transportTypes,
  ]);

  const uploadProps: UploadProps = {
    accept: ACCEPTED_TYPES,
    multiple: false,
    showUploadList: false,
    disabled: isProcessing,
    beforeUpload: (file) => {
      void startAnalysis(file);
      return false;
    },
  };

  const handleContinue = () => {
    setV2Step(1);
  };

  const handleSkip = async () => {
    try {
      const bookingId = await ensureDraftBooking(selectedCompany, state.id);
      markV2Booking(bookingId);
      dispatch({ type: "SET_BOOKING_ID", payload: bookingId });
      await updateStep(1, { id: bookingId });
      setV2Step(1);
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : t("bookingWizardV2.invoice.uploadFailed")
      );
    }
  };

  const showAnimation =
    isProcessing ||
    analysisPhase === "complete" ||
    (analysisPhase !== "idle" && analysisPhase !== "error");

  return (
    <WizardStep
      onNext={analysisPhase === "complete" ? handleContinue : undefined}
      nextButtonText={
        analysisPhase === "complete"
          ? t("bookingWizardV2.draftPreview.continueButton")
          : t("bookingWizardV2.invoice.reviewExtracted")
      }
      nextDisabled={analysisPhase !== "complete"}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <Typography.Title level={3} style={{ textAlign: "center", marginBottom: 8 }}>
          {analysisPhase === "complete"
            ? t("bookingWizardV2.draftPreview.pageTitle")
            : t("bookingWizardV2.invoice.title")}
        </Typography.Title>
        {analysisPhase !== "complete" && (
          <Paragraph
            type="secondary"
            style={{ textAlign: "center", marginBottom: 32 }}
          >
            {t("bookingWizardV2.invoice.subtitle")}
          </Paragraph>
        )}

        {analysisError && (
          <Alert
            type="error"
            message={analysisError}
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setAnalysisError(null)}
          />
        )}

        {existingPath && !selectedFile && analysisPhase === "idle" && (
          <Alert
            type="info"
            showIcon
            icon={<FilePdfOutlined />}
            message={t("bookingWizardV2.invoice.existingInvoice")}
            description={
              <Button type="link" onClick={loadExistingAnalysis} loading={isProcessing}>
                {t("bookingWizardV2.invoice.loadExistingAnalysis")}
              </Button>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {!showAnimation && (
          <Dragger
            {...uploadProps}
            style={{
              padding: "24px 16px",
              borderRadius: token.borderRadiusLG,
              background: token.colorFillAlter,
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: token.colorPrimary, fontSize: 48 }} />
            </p>
            <p className="ant-upload-text">{t("bookingWizardV2.invoice.dragDrop")}</p>
            <p className="ant-upload-hint">{t("bookingWizardV2.invoice.formats")}</p>
          </Dragger>
        )}

        {analysisPhase === "complete" && analysisResult ? (
          <BookingDraftPreview
            result={analysisResult}
            missingFields={missingFields}
            fileName={uploadedFileName}
          />
        ) : (
          showAnimation && (
            <InvoiceAnalysisAnimation
              phase={analysisPhase}
              fileName={uploadedFileName}
            />
          )
        )}

        {!showAnimation && (
          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-start" }}>
            <Button onClick={handleSkip} disabled={isProcessing}>
              {t("bookingWizardV2.invoice.skipManual")}
            </Button>
          </div>
        )}
      </div>
    </WizardStep>
  );
};
